import type { ColumnLayout } from "@unbox/api";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSaveTableColumns, useUserPreferences } from "@/api/user-preferences";
import { useDebounce } from "@/hooks/use-debounce";
import { type ColumnCatalog, defaultLayout, normalize, visibleIds } from "./column-layout";
import type { UnboxTableColumn, UnboxTableRow } from "./unbox-table.types";

const SAVE_LAYOUT_DELAY_MS = 1000;

export const useColumnLayout = <Row extends UnboxTableRow>(
  catalog: ColumnCatalog,
  columns: UnboxTableColumn<Row>[],
) => {
  const { data } = useUserPreferences();
  const { mutate: saveLayout } = useSaveTableColumns(catalog.tableKey);
  const saved = data?.tableColumns[catalog.tableKey];

  // Edits apply to the table straight away and are saved once they settle, so a burst of clicks
  // in the panel is one request.
  const [draft, setDraft] = useState<ColumnLayout | null>(null);
  const debouncedDraft = useDebounce(draft, SAVE_LAYOUT_DELAY_MS);
  const unsaved = useRef<ColumnLayout | null>(null);

  useEffect(() => {
    if (!debouncedDraft || unsaved.current !== debouncedDraft) return;
    unsaved.current = null;
    saveLayout(debouncedDraft);
  }, [debouncedDraft, saveLayout]);

  // An edit still waiting on the delay is saved when the table goes away.
  useEffect(
    () => () => {
      if (unsaved.current) saveLayout(unsaved.current);
    },
    [saveLayout],
  );

  const setLayout = (next: ColumnLayout) => {
    unsaved.current = next;
    setDraft(next);
  };

  const layout = useMemo(() => normalize(catalog, draft ?? saved), [catalog, draft, saved]);

  const visibleColumns = useMemo(() => {
    const [identity, ...rest] = columns;
    const catalogIds = new Set(catalog.options.map((option) => option.id));
    const byId = new Map(rest.map((column) => [column.id, column]));
    const configured = visibleIds(layout).flatMap((id) => byId.get(id) ?? []);
    const unconfigured = rest.filter((column) => !catalogIds.has(column.id));
    return identity ? [identity, ...configured, ...unconfigured] : [];
  }, [catalog, columns, layout]);

  const labels = useMemo(() => new Map(columns.map((column) => [column.id, column.label])), [columns]);

  return {
    layout,
    visibleColumns,
    labels,
    identityLabel: columns[0]?.label ?? "",
    setLayout,
    resetLayout: () => setLayout(defaultLayout(catalog)),
  };
};
